'use client';

import { useTranslation } from '@/context/LanguageContext';
import type { ProgramStudiResponse } from '@/types/prodi';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGraduationCap, faUsers, faCertificate, faAward, faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';

interface ProdiSectionProps {
  prodiData: ProgramStudiResponse;
}

export default function ProdiSection({ prodiData }: ProdiSectionProps) {
  const { language } = useTranslation();

  const getAccreditationColor = (akreditasi: string) => {
    switch (akreditasi) {
      case 'A':
        return 'from-green-500 to-emerald-600';
      case 'B':
        return 'from-blue-500 to-cyan-600';
      case 'C':
        return 'from-orange-500 to-amber-600';
      default:
        return 'from-gray-500 to-slate-600';
    }
  };

  const getAccreditationBadge = (akreditasi: string) => {
    if (!akreditasi) return null;
    
    return (
      <div className="absolute top-4 right-4 z-10">
        <div className={`bg-gradient-to-br ${getAccreditationColor(akreditasi)} text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-2`}>
          <FontAwesomeIcon icon={faAward} className="text-sm" />
          <span className="font-bold text-sm">{akreditasi}</span>
        </div>
      </div>
    );
  };

  return (
    <section className="py-20 bg-gradient-to-b from-unmulPale to-white relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-unmulYellow/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-unmulGreen/10 rounded-full blur-3xl"></div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16" data-aos="fade-up">
          <span className="inline-block bg-gradient-to-r from-unmulGreen to-emerald-600 text-white px-6 py-2 rounded-full text-sm font-bold uppercase tracking-wider mb-4 shadow-lg">
            {language === 'en' ? 'Study Programs' : 'Program Studi'}
          </span>
          <h2 className="text-3xl md:text-5xl font-serif font-bold text-gray-800 mb-4">
            {language === 'en' ? 'Our Study Programs' : 'Program Studi Kami'}
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            {language === 'en' 
              ? `Discover ${prodiData.metadata.total} quality accredited study programs designed to prepare you for the future` 
              : `Temukan ${prodiData.metadata.total} program studi berkualitas terakreditasi yang dirancang untuk mempersiapkan masa depan Anda`}
          </p>
        </div>

        {/* Programs Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {prodiData.data.map((prodi, index) => (
            <div 
              key={prodi.prodikode}
              className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-unmulGreen/30 relative"
              data-aos="fade-up"
              data-aos-delay={index * 50}
            >
              {/* Card Header with Solid Green */}
              <div className="h-3 bg-unmulGreen"></div>

              {/* Card Content */}
              <div className="p-6">
                {/* Program Name */}
                <h3 className="text-xl font-bold text-gray-800 mb-4 min-h-[3.5rem] line-clamp-2 group-hover:text-unmulGreen transition-colors">
                  {prodi.prodinamaresmi}
                </h3>

                {/* Info List */}
                <div className="space-y-3">
                  {/* Accreditation */}
                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="w-10 h-10 bg-unmulGreen rounded-lg flex items-center justify-center text-white shrink-0">
                      <FontAwesomeIcon icon={faCertificate} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500 font-medium uppercase mb-1">
                        {language === 'en' ? 'Accreditation' : 'Akreditasi'}
                      </p>
                      <p className="text-sm font-semibold text-gray-800">
                        {prodi.prodiakreditasi || '-'}
                      </p>
                    </div>
                  </div>

                  {/* Degree */}
                  <div className="flex items-start gap-3 p-3 bg-unmulPale/50 rounded-lg">
                    <div className="w-10 h-10 bg-unmulYellow rounded-lg flex items-center justify-center text-white shrink-0">
                      <FontAwesomeIcon icon={faGraduationCap} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500 font-medium uppercase mb-1">
                        {language === 'en' ? 'Degree' : 'Gelar'}
                      </p>
                      <p className="text-sm font-semibold text-gray-800 leading-tight">
                        {prodi.prodigelarkelulusan || '-'}
                      </p>
                    </div>
                  </div>

                  {/* Capacity */}
                  <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
                    <div className="w-10 h-10 bg-unmulGreen rounded-lg flex items-center justify-center text-white shrink-0">
                      <FontAwesomeIcon icon={faUsers} />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 font-medium uppercase mb-1">
                        {language === 'en' ? 'Student Capacity' : 'Daya Tampung'}
                      </p>
                      <p className="text-2xl font-bold text-unmulGreen">
                        {prodi.prodidayatampung > 0 ? prodi.prodidayatampung : '-'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Hover Effect Overlay */}
              <div className="absolute inset-0 border-2 border-unmulGreen rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"></div>
            </div>
          ))}
        </div>

        {/* Footer Note */}
        {prodiData.metadata.total > prodiData.data.length && (
          <div className="mt-12 text-center" data-aos="fade-up">
            <p className="text-gray-500 text-sm">
              {language === 'en' 
                ? `Showing ${prodiData.data.length} of ${prodiData.metadata.total} programs`
                : `Menampilkan ${prodiData.data.length} dari ${prodiData.metadata.total} program`}
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
